import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { placementsAPI, enrollmentsAPI, mastersAPI } from '../services/api';
import Modal from '../components/Modal';
import SearchableSelect from '../components/Form/SearchableSelect';
import LoadingSpinner from '../components/LoadingSpinner';
import MessageDisplay from '../components/MessageDisplay';
import FilterBar from '../components/FilterBar/FilterBar';
import DataCard from '../components/DataCard/DataCard';
import FormField from '../components/Form/FormField';
import FormSelect from '../components/Form/FormSelect';
import FormTextarea from '../components/Form/FormTextarea';
import FormSection from '../components/Form/FormSection';
import { formatDate } from '../utils/formatUtils';
import useAsyncOperation from '../hooks/useAsyncOperation';
import { normalizeApiListResponse } from '../utils/apiListNormalize';
import StaffPageShell from '../components/StaffPageShell';

const PlacementManagementPage = () => {
  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return `₹${parseFloat(amount).toLocaleString('en-IN')}`;
  };
  const navigate = useNavigate();
  const [placements, setPlacements] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const { loading, error, success, execute, setError, setSuccess } = useAsyncOperation();
  const [showModal, setShowModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editingPlacement, setEditingPlacement] = useState(null);
  const [formData, setFormData] = useState({
    enrollmentId: '',
    companyId: '',
    joiningDate: '',
    jobRole: '',
    startingPackage: '',
    currentPackage: '',
    feedbackRemarks: '',
    testimonyReceived: false,
    partOfAlumniGroup: false
  });

  useEffect(() => {
    fetchPlacements();
    fetchReferenceData();
  }, []);

  const fetchReferenceData = async () => {
    try {
      const [enrollmentsRes, companiesRes] = await Promise.all([
        enrollmentsAPI.getAll(),
        mastersAPI.list('company')
      ]);

      setEnrollments(normalizeApiListResponse(enrollmentsRes.data));
      setCompanies(normalizeApiListResponse(companiesRes.data));
    } catch (err) {
      console.error('Error fetching reference data:', err);
    }
  };

  const fetchPlacements = async () => {
    const response = await execute(async () => await placementsAPI.getAll());
    if (response?.data !== undefined) {
      setPlacements(normalizeApiListResponse(response.data));
    }
  };

  const handleCreate = () => {
    setEditingPlacement(null);
    setFormData({
      enrollmentId: '',
      companyId: '',
      joiningDate: '',
      jobRole: '',
      startingPackage: '',
      currentPackage: '',
      feedbackRemarks: '',
      testimonyReceived: false,
      partOfAlumniGroup: false
    });
    setShowModal(true);
  };

  const handleEdit = (placement) => {
    setEditingPlacement(placement);
    setFormData({
      enrollmentId: placement.enrollmentId?.toString() || '',
      companyId: placement.companyId?.toString() || '',
      joiningDate: placement.joiningDate ? placement.joiningDate.split('T')[0] : '',
      jobRole: placement.jobRole || '',
      startingPackage: placement.startingPackage?.toString() || '',
      currentPackage: placement.currentPackage?.toString() || '',
      feedbackRemarks: placement.feedbackRemarks || '',
      testimonyReceived: placement.testimonyReceived || false,
      partOfAlumniGroup: placement.partOfAlumniGroup || false
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const response = await execute(async () => {
      if (editingPlacement) {
        return await placementsAPI.update(editingPlacement.id, formData);
      } else {
        return await placementsAPI.create(formData);
      }
    }, { 
      successMessage: `Placement ${editingPlacement ? 'updated' : 'created'} successfully!` 
    });
    
    if (response) {
      setShowModal(false);
      setEditingPlacement(null);
      fetchPlacements();
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;

    const response = await execute(
      async () => await placementsAPI.delete(deleteConfirm.id),
      { successMessage: 'Placement deleted successfully!' }
    );
    
    if (response) {
      setDeleteConfirm(null);
      fetchPlacements();
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  const filteredPlacements = placements.filter(placement => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const studentName = placement.enrollment?.student?.fullName?.toLowerCase() || '';
    const companyName = placement.company?.companyName?.toLowerCase() || '';
    const jobRole = placement.jobRole?.toLowerCase() || '';
    const batchName = placement.enrollment?.batch?.batchName?.toLowerCase() || '';
    return studentName.includes(term) || companyName.includes(term) || jobRole.includes(term) || batchName.includes(term);
  });

  return (
    <StaffPageShell title="Placements" wide>
        <div className="flex justify-end items-center mb-6">
          <button
            onClick={handleCreate}
            className="px-6 py-2 rounded-lg font-medium theme-button-primary flex items-center gap-2"
          >
            <span>+</span> Add Placement
          </button>
        </div>

        <MessageDisplay message={error || success} />

        {/* Search Filter */}
        <FilterBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search by student name, company, job role, or batch..."
        />

        {loading && !placements.length ? (
          <LoadingSpinner message="Loading placements..." />
        ) : filteredPlacements.length === 0 ? (
          <div className="text-center py-12 theme-card rounded-lg">
            <p className="text-lg theme-text-secondary mb-4">
              {searchTerm ? 'No placements found matching your search' : 'No placements found'}
            </p>
            {!searchTerm && (
              <button
                onClick={handleCreate}
                className="px-6 py-2 rounded-lg font-medium theme-button-primary"
              >
                Add Your First Placement
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {filteredPlacements.map((placement) => (
              <DataCard
                key={placement.id}
                title={placement.enrollment?.student?.fullName || 'Unknown Student'}
                badges={[
                  { label: placement.company?.companyName || 'N/A', className: 'bg-blue-100 text-blue-700' }
                ]}
                metadata={[
                  { label: 'Job Role', value: placement.jobRole || 'N/A' },
                  { label: 'Joining Date', value: formatDate(placement.joiningDate) },
                  { label: 'Batch', value: placement.enrollment?.batch?.batchName || 'N/A' },
                  { label: 'Course', value: placement.enrollment?.batch?.course?.courseName || 'N/A' }
                ]}
                actions={[
                  { label: 'Edit', onClick: () => handleEdit(placement), variant: 'edit' },
                  { label: 'Delete', onClick: () => setDeleteConfirm(placement), variant: 'delete' }
                ]}
              >
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg border border-green-200 dark:border-green-800">
                    <div className="text-xs font-medium text-green-700 dark:text-green-300 mb-1">Starting Package</div>
                    <div className="text-2xl font-bold text-green-800 dark:text-green-200">
                      {formatCurrency(placement.startingPackage)}
                    </div>
                  </div>
                  {placement.currentPackage && (
                    <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <div className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">Current Package</div>
                      <div className="text-2xl font-bold text-blue-800 dark:text-blue-200">
                        {formatCurrency(placement.currentPackage)}
                      </div>
                      {placement.startingPackage && placement.currentPackage && (
                        <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                          Growth: {((parseFloat(placement.currentPackage) - parseFloat(placement.startingPackage)) / parseFloat(placement.startingPackage) * 100).toFixed(1)}%
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {placement.feedbackRemarks && (
                  <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <div className="text-sm font-medium theme-text-secondary mb-1">Feedback & Remarks</div>
                    <p className="theme-text text-sm">{placement.feedbackRemarks}</p>
                  </div>
                )}

                <div className="mt-4 flex flex-wrap gap-2">
                  {placement.testimonyReceived && (
                    <span className="px-3 py-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 text-xs font-medium rounded-full flex items-center gap-1">
                      <span>✅</span> Testimony Received
                    </span>
                  )}
                  {placement.partOfAlumniGroup && (
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 text-xs font-medium rounded-full flex items-center gap-1">
                      <span>👥</span> Alumni Group
                    </span>
                  )}
                </div>
              </DataCard>
            ))}
          </div>
        )}

        {/* Create/Edit Modal */}
        <Modal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setEditingPlacement(null);
          }}
          title={editingPlacement ? 'Edit Placement' : 'Add New Placement'}
          size="lg"
        >
          <>
            <form id="placement-form" onSubmit={handleSubmit} className="space-y-6">
                  {/* Basic Information Section */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b border-gray-200 dark:border-gray-700">
                      Basic Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-700 dark:text-gray-200 mb-2 font-medium text-sm">
                          Student Enrollment <span className="text-red-500">*</span>
                        </label>
                        <SearchableSelect
                          required
                          value={formData.enrollmentId}
                          onChange={(e) => setFormData({ ...formData, enrollmentId: e.target.value })}
                          disabled={!!editingPlacement}
                          className="w-full"
                          placeholder="Select Enrollment"
                          options={enrollments}
                          getOptionValue={(enrollment) => enrollment.id}
                          getOptionLabel={(enrollment) =>
                            `${enrollment.student?.fullName || 'Unknown'} - ${enrollment.batch?.batchName || 'N/A'}`
                          }
                        />
                        {editingPlacement && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">Enrollment cannot be changed after creation</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-gray-700 dark:text-gray-200 mb-2 font-medium text-sm">
                          Company <span className="text-red-500">*</span>
                        </label>
                        <SearchableSelect
                          required
                          value={formData.companyId}
                          onChange={(e) => setFormData({ ...formData, companyId: e.target.value })}
                          className="w-full"
                          placeholder="Select Company"
                          options={companies}
                          getOptionValue={(company) => company.id}
                          getOptionLabel={(company) => company.value || company.companyName}
                        />
                      </div>

                      <div>
                        <label className="block text-gray-700 dark:text-gray-200 mb-2 font-medium text-sm">
                          Joining Date <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          required
                          value={formData.joiningDate}
                          onChange={(e) => setFormData({ ...formData, joiningDate: e.target.value })}
                          className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg theme-input focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-gray-700 dark:text-gray-200 mb-2 font-medium text-sm">Job Role</label>
                        <input
                          type="text"
                          value={formData.jobRole}
                          onChange={(e) => setFormData({ ...formData, jobRole: e.target.value })}
                          className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg theme-input focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                          placeholder="e.g., Software Engineer, VLSI Designer"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Salary Information Section */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b border-gray-200 dark:border-gray-700">
                      Salary Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-700 dark:text-gray-200 mb-2 font-medium text-sm">Starting Package (₹)</label>
                        <div className="relative">
                          <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500">₹</span>
                          <input
                            type="number"
                            min="0"
                            step="1000"
                            value={formData.startingPackage}
                            onChange={(e) => setFormData({ ...formData, startingPackage: e.target.value })}
                            className="w-full pl-8 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg theme-input focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                            placeholder="500000"
                          />
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">Annual starting salary</p>
                      </div>

                      <div>
                        <label className="block text-gray-700 dark:text-gray-200 mb-2 font-medium text-sm">Current Package (₹)</label>
                        <div className="relative">
                          <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500">₹</span>
                          <input
                            type="number"
                            min="0"
                            step="1000"
                            value={formData.currentPackage}
                            onChange={(e) => setFormData({ ...formData, currentPackage: e.target.value })}
                            className="w-full pl-8 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg theme-input focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                            placeholder="600000"
                          />
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">Current annual salary (if different)</p>
                      </div>
                    </div>
                  </div>

                  {/* Additional Information Section */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b border-gray-200 dark:border-gray-700">
                      Additional Information
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-gray-700 dark:text-gray-200 mb-2 font-medium text-sm">Feedback & Remarks</label>
                        <textarea
                          value={formData.feedbackRemarks}
                          onChange={(e) => setFormData({ ...formData, feedbackRemarks: e.target.value })}
                          className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg theme-input focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none"
                          rows="4"
                          placeholder="Enter feedback or remarks about the placement..."
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <label className="flex items-center p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors">
                          <input
                            type="checkbox"
                            checked={formData.testimonyReceived}
                            onChange={(e) => setFormData({ ...formData, testimonyReceived: e.target.checked })}
                            className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-2 focus:ring-green-500 cursor-pointer"
                          />
                          <span className="ml-3 text-gray-700 dark:text-gray-200 font-medium flex items-center gap-2">
                            <span>✅</span> Testimony Received
                          </span>
                        </label>
                        <label className="flex items-center p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors">
                          <input
                            type="checkbox"
                            checked={formData.partOfAlumniGroup}
                            onChange={(e) => setFormData({ ...formData, partOfAlumniGroup: e.target.checked })}
                            className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-2 focus:ring-purple-500 cursor-pointer"
                          />
                          <span className="ml-3 text-gray-700 dark:text-gray-200 font-medium flex items-center gap-2">
                            <span>👥</span> Part of Alumni Group
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                </form>
                <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingPlacement(null);
                    }}
                    className="px-6 py-2.5 rounded-lg font-medium theme-button-secondary transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    form="placement-form"
                    disabled={loading}
                    className="px-6 py-2.5 rounded-lg font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        {editingPlacement ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      <>
                        {editingPlacement ? (
                          <>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Update Placement
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Create Placement
                          </>
                        )}
                      </>
                    )}
                  </button>
                </div>
            </>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          isOpen={!!deleteConfirm}
          onClose={() => setDeleteConfirm(null)}
          title="Delete Placement"
          size="sm"
        >
          <p className="theme-text-secondary mb-4">
            Are you sure you want to delete the placement for <strong>{deleteConfirm?.enrollment?.student?.fullName || 'this student'}</strong> at <strong>{deleteConfirm?.company?.companyName || 'this company'}</strong>?
          </p>
          <p className="text-sm text-yellow-600 dark:text-yellow-400 mb-4">
            This will also update the enrollment's placement status.
          </p>
          <div className="flex space-x-4">
            <button
              onClick={handleDelete}
              disabled={loading}
              className="flex-1 px-4 py-2 rounded-lg font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
            >
              {loading ? 'Deleting...' : 'Delete'}
            </button>
            <button
              onClick={() => setDeleteConfirm(null)}
              className="flex-1 px-4 py-2 rounded-lg font-medium theme-button-secondary"
            >
              Cancel
            </button>
          </div>
        </Modal>
    </StaffPageShell>
  );
};

export default PlacementManagementPage;
